// src/pages/PassengersPage.tsx
// Passengers management page

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Filter,
  Download,
  Eye,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Shield,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import adminUserService from '../services/adminUserService';
import { User } from '../types/user';
import './PassengersPage.css';
import Navigation from '../components/common/Navigation';

const PassengersPage: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [passengers, setPassengers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [isVerified, setIsVerified] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Load passengers
  useEffect(() => {
    loadPassengers();
  }, [page, search, status, isVerified]);

  const loadPassengers = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch users with role 'passenger' OR 'both'
      const response = await adminUserService.getUsers({
        page,
        limit: 10,
        search,
        role: '', // We'll filter on frontend to get both passenger and both
        status,
        isVerified: isVerified === '' ? null : isVerified === 'true',
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      // Filter to include only passengers and "both"
      const passengerUsers = response.data.filter(
        (user) => user.role === 'passenger' || user.role === 'both'
      );

      setPassengers(passengerUsers);
      setTotalPages(response.pagination.totalPages);
      setTotal(passengerUsers.length);
    } catch (err: any) {
      setError(err.message || 'Failed to load passengers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchTerm: string) => {
    setSearch(searchTerm);
    setPage(1);
  };

  const handleRefresh = () => {
    loadPassengers();
  };

  const handleExport = async () => {
    try {
      await adminUserService.exportUsers({ role: 'passenger', status });
    } catch (err: any) {
      setError(err.message || 'Failed to export passengers');
    }
  };

  const handleViewPassenger = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: { bg: '#d1fae5', color: '#065f46' },
      suspended: { bg: '#fed7aa', color: '#92400e' },
      banned: { bg: '#fecaca', color: '#991b1b' },
    };

    const style = styles[status as keyof typeof styles] || styles.active;

    return (
      <span className="badge" style={{ background: style.bg, color: style.color }}>
        {status}
      </span>
    );
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <>
      <Navigation />
    <div className="passengers-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <Users size={32} className="header-icon" />
          <div>
            <h1>Passengers Management</h1>
            <p>Manage all passengers and their accounts</p>
          </div>
        </div>
        <div className="header-actions">
          <button onClick={() => navigate('/users')} className="btn-secondary">
            All Users
          </button>
          <button onClick={handleRefresh} className="btn-icon" title="Refresh">
            <RefreshCw size={20} />
          </button>
          <button onClick={handleExport} className="btn-secondary">
            <Download size={20} />
            Export
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-box">
          <div className="stat-label">Total Passengers</div>
          <div className="stat-value">{total}</div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Active</div>
          <div className="stat-value">
            {passengers.filter((p) => p.status === 'active').length}
          </div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Verified</div>
          <div className="stat-value">
            {passengers.filter((p) => p.isVerified).length}
          </div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Total Rides</div>
          <div className="stat-value">
            {passengers.reduce((sum, p) => sum + (p.passengerProfile?.totalRides || 0), 0)}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="filters-section">
        <div className="search-bar">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search passengers..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`btn-filter ${showFilters ? 'active' : ''}`}
        >
          <Filter size={20} />
          Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-content">
            <div className="filter-group">
              <label>Verification Status</label>
              <select value={isVerified} onChange={(e) => setIsVerified(e.target.value)}>
                <option value="">All</option>
                <option value="true">Verified</option>
                <option value="false">Unverified</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Account Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="banned">Banned</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Passengers Table */}
      {loading ? (
        <div className="table-loading">
          <div className="spinner"></div>
          <p>Loading passengers...</p>
        </div>
      ) : passengers.length === 0 ? (
        <div className="table-empty">
          <Users size={48} />
          <p>No passengers found</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="passengers-table">
            <thead>
              <tr>
                <th>Passenger</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Verified</th>
                <th>Rating</th>
                <th>Total Rides</th>
                <th>Payment</th>
                <th>Registered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {passengers.map((passenger) => (
                <tr key={passenger.id}>
                  <td>
                    <div className="user-cell">
                      <div className="user-avatar">
                        {passenger.profileImage ? (
                          <img src={passenger.profileImage} alt={passenger.firstName} />
                        ) : (
                          <div className="avatar-placeholder">
                            {passenger.firstName[0]}
                            {passenger.lastName[0]}
                          </div>
                        )}
                      </div>
                      <div className="user-info">
                        <div className="user-name">
                          {passenger.firstName} {passenger.lastName}
                        </div>
                        {passenger.role === 'both' && (
                          <div className="user-meta">
                            <Shield size={12} />
                            Also a Driver
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{passenger.email}</td>
                  <td>{passenger.phoneNumber || 'N/A'}</td>
                  <td>{getStatusBadge(passenger.status)}</td>
                  <td>
                    {passenger.isVerified ? (
                      <CheckCircle size={20} className="icon-success" />
                    ) : (
                      <XCircle size={20} className="icon-muted" />
                    )}
                  </td>
                  <td>
                    {passenger.passengerProfile?.rating ? (
                      <span>⭐ {passenger.passengerProfile.rating.toFixed(1)}</span>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>{passenger.passengerProfile?.totalRides || 0}</td>
                  <td>
                    {passenger.passengerProfile?.preferredPaymentMethod || 'N/A'}
                  </td>
                  <td>{formatDate(passenger.createdAt)}</td>
                  <td>
                    <button
                      onClick={() => handleViewPassenger(passenger.id)}
                      className="btn-icon"
                      title="View Details"
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="pagination">
        <div className="pagination-info">
          Showing {passengers.length} of {total} passengers
        </div>
        <div className="pagination-controls">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="btn-icon"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="page-number">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
            className="btn-icon"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default PassengersPage;